import { useEffect, useRef, useState, useCallback } from 'react';

import {
  WIDGET_MESSAGE,
  ORIGIN_TRUST_STATUS,
  OriginTrustStatusType,
  WidgetMessageType,
  WidgetMessageEvent,
  WidgetMessage,
  isValidWidgetMessageType,
} from '@hospitality-consumer-facing/lsk-reservation/shared';

type MessageHandler = (data?: unknown) => void;

interface UseWidgetCommunicationOptions {
  onUnauthorizedOrigin: (origin: string) => void;
  handlers?: Partial<Record<WidgetMessageType, MessageHandler>>;
}

// Default trusted origin patterns for iframe embedding
// Add CSP frame-ancestors validation when implementing Content Security Policy
const MERCHANT_TRUSTED_WEBSITES: RegExp[] = [];

function isOriginAllowed(origin: string): boolean {
  if (MERCHANT_TRUSTED_WEBSITES.length) {
    return MERCHANT_TRUSTED_WEBSITES.some((pattern) => pattern.test(origin));
  }
  // For now we allow all origins, until user not filled trusted websites
  return true;
}

export function useWidgetCommunication({
  onUnauthorizedOrigin,
  handlers,
}: UseWidgetCommunicationOptions) {
  const [parentOrigin, setParentOrigin] = useState<string | null>(null);
  const [originTrustStatus, setOriginTrustStatus] = useState<OriginTrustStatusType>(
    ORIGIN_TRUST_STATUS.PENDING,
  );
  const [isStandalone, setIsStandalone] = useState<boolean>(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const onUnauthorizedOriginRef = useRef(onUnauthorizedOrigin);
  onUnauthorizedOriginRef.current = onUnauthorizedOrigin;

  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    // Check if we're in an iframe
    if (window.self === window.top) {
      // Standalone mode - widget accessed directly
      setIsStandalone(true);
      setOriginTrustStatus(ORIGIN_TRUST_STATUS.TRUSTED);
      setLastError(null);
      return undefined;
    }

    // Handle incoming messages from parent window
    const handleMessage = ({ data, origin }: WidgetMessageEvent) => {
      if (!data || typeof data !== 'object') {
        return;
      }

      if (!isValidWidgetMessageType(data.type)) {
        return;
      }

      if (data.type === WIDGET_MESSAGE.HANDSHAKE_RESPONSE) {
        const trustStatus = isOriginAllowed(origin)
          ? ORIGIN_TRUST_STATUS.TRUSTED
          : ORIGIN_TRUST_STATUS.UNTRUSTED;

        setParentOrigin(origin);
        setOriginTrustStatus(trustStatus);

        if (trustStatus === ORIGIN_TRUST_STATUS.TRUSTED) {
          setLastError(null);
        } else {
          setLastError(`Untrusted origin in handshake: ${origin}`);
          onUnauthorizedOriginRef.current?.(origin);
        }
        return;
      }

      const handler = handlersRef.current?.[data.type];
      if (handler) {
        handler(data.payload);
      }
    };

    window.addEventListener('message', handleMessage);

    // Send handshake request to parent window
    try {
      window.parent.postMessage(
        {
          type: WIDGET_MESSAGE.HANDSHAKE_REQUEST,
          timestamp: Date.now(),
        },
        '*',
      );
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setLastError(`Origin detection failed: ${errorMsg}`);
      console.error('[Widget] Origin detection error:', error);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const sendMessage = useCallback(
    (message: WidgetMessage) => {
      try {
        if (isStandalone) {
          return true;
        }

        const targetOrigin = parentOrigin ?? '*';
        window.parent.postMessage(message, targetOrigin);

        setLastError(null);
        return true;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        setLastError(`Failed to send message: ${errorMsg}`);
        console.error('[Widget] Send message error:', error);
        return false;
      }
    },
    [isStandalone, parentOrigin],
  );

  return {
    sendMessage,
    parentOrigin,
    originTrustStatus,
    isStandalone,
    lastError,
  };
}
